'use client';

import { ButtonHTMLAttributes, ForwardedRef, ReactNode } from 'react';

import { Styles, css, cx } from '@flipster/styled-system/css';
import { button, type ButtonVariantProps } from '@flipster/styled-system/recipes';

import { IsomorphicLink, IsomorphicLinkProps } from '../design-utils/isomorphic-link';
import { useButtonLoadingState } from './_button-shared';
import { Spinner } from './spinner';

interface BaseProps {
  // variants
  hierarchy?: ButtonVariantProps['hierarchy'];
  color?: ButtonVariantProps['color'];
  size?: ButtonVariantProps['size'];
  type?: ButtonVariantProps['type'];
  // slots
  icon?: ReactNode;
  label?: ReactNode;
  subLabel?: ReactNode;
  css?: Styles;
}

type ButtonElementProps = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps | 'className' | 'children' | 'type'> & {
    ref?: ForwardedRef<HTMLButtonElement>;
    isLoading?: boolean;
    withSpinner?: boolean;
    /** HTML button type attribute. Use 'submit' for form submission. */
    htmlType?: 'button' | 'submit' | 'reset';
  };
type LinkElementProps = BaseProps &
  Omit<IsomorphicLinkProps, keyof BaseProps | 'className' | 'children'> & {
    ref?: ForwardedRef<HTMLAnchorElement>;
  };
export type ButtonProps<T extends 'button' | 'a' | undefined = 'button'> = { as?: T } & (T extends 'button'
  ? ButtonElementProps
  : LinkElementProps);

const ButtonComponent = ({
  hierarchy,
  color,
  size,
  type,
  icon,
  label,
  subLabel,
  css: cssProps,
  withSpinner,
  isLoading,
  disabled,
  onClick,
  ref,
  htmlType = 'button',
  ...htmlProps
}: ButtonElementProps) => {
  const { waiting, handleClick } = useButtonLoadingState(onClick);

  const classes = button({ hierarchy, color, size, type });
  const showSpinner = withSpinner && (isLoading || waiting);
  const isNeutral = color === 'neutral';

  return (
    <button
      ref={ref}
      type={htmlType}
      role='button'
      className={cx(classes.root, css(cssProps))}
      data-loading={showSpinner ? 'true' : undefined}
      aria-label={showSpinner ? 'Loading' : undefined}
      disabled={isLoading || waiting || disabled}
      onClick={handleClick}
      {...htmlProps}
    >
      {icon && (
        <div aria-label='icon' className={classes.icon}>
          {icon}
        </div>
      )}
      {label && <div className={classes.label}>{label}</div>}
      {subLabel && <div className={classes.subLabel}>{subLabel}</div>}
      {showSpinner && (
        <div className={classes.spinner}>
          <Spinner color={isNeutral ? 'gray' : 'white'} />
        </div>
      )}
    </button>
  );
};

// Link 형식은 isLoading 이 필요없으므로 제외
const LinkComponent = ({
  hierarchy,
  color,
  size,
  type,
  icon,
  label,
  ref,
  subLabel,
  css: cssProps,
  ...htmlProps
}: LinkElementProps) => {
  const classes = button({ hierarchy, color, size, type });

  return (
    <IsomorphicLink ref={ref} className={cx(classes.root, css(cssProps))} {...htmlProps}>
      {icon && <div className={classes.icon}>{icon}</div>}
      {label && <div className={classes.label}>{label}</div>}
      {subLabel && <div className={classes.subLabel}>{subLabel}</div>}
    </IsomorphicLink>
  );
};

export const Button = <T extends 'button' | 'a' | undefined = 'button'>({
  as = 'button',
  ...htmlProps
}: ButtonProps<T>) => {
  if (as === 'button') {
    return <ButtonComponent {...(htmlProps as ButtonElementProps)} />;
  }
  if (as === 'a') {
    return <LinkComponent {...(htmlProps as LinkElementProps)} />;
  }
  return null;
};
Button.displayName = 'Button';
